import { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import isEmpty from 'lodash/isEmpty';
import Paper from 'components/system/Paper';
import Loading from 'components/system/Loading';
import { useGetUsage } from 'hooks/useBillingAccount';
import { createGradientColoredColumn } from 'utils/chart';
import { gradientDarkColor, gradientLightColor } from 'utils/constants';
import usePaymentsStore from 'store/paymentStore';
import colors from 'styles/colors';
import UsageChartHeader from './UsageChartHeader';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

export const options = {
  responsive: true,
  maintainAspectRatio: false,
  maxBarThickness: 60,
  borderRadius: 4,
  layout: {
    padding: {
      bottom: 20,
    },
  },
  plugins: {
    tooltip: {
      callbacks: {
        label: (context) => `${context.dataset.label}: $${context.parsed.y}`,
      },
    },
  },
};

const UsageChart = () => {
  useGetUsage();
  const { chart, totalUsage, isLoading } = usePaymentsStore((state) => state.usage);

  // eslint-disable-next-line consistent-return
  const usageData = useMemo(() => {
    if (!isEmpty(chart)) {
      const usage = chart.map((monthlyUsage) => monthlyUsage.value);
      const usageMonths = chart.map((monthlyUsage) => monthlyUsage.month);

      const maxValue = Math.max(...usage);

      return {
        labels: usageMonths,
        datasets: [
          {
            label: 'Costs',
            data: usage,
            backgroundColor: createGradientColoredColumn(
              maxValue,
              gradientDarkColor,
              gradientLightColor,
            ),
          },
        ],
      };
    }
  }, [chart]);

  let innerContent = null;

  if (isLoading) {
    innerContent = <Loading />;
  } else if (!isEmpty(chart)) {
    innerContent = <Bar options={options} data={usageData} />;
  } else {
    innerContent = (
      <Box
        sx={{
          height: '100%',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="body2" color={colors.text.primary}>
          Your monthly usage will appear here 🎉
        </Typography>
      </Box>
    );
  }

  return (
    <Paper sx={{ height: '156px' }}>
      <UsageChartHeader />
      {innerContent}
    </Paper>
  );
};

export default UsageChart;
